// Vanuatu National Syllabus: Agriculture Senior Secondary Years 11-13
// Based on official syllabus document revised February 2023

export const agricultureCurriculum = {
  year11: {
    title: "Year 11 Agriculture",
    description: "Foundation agricultural concepts covering sustainable farm ecosystems, soil management, plant and animal production, crop and animal protection, and farm management economics",
    hoursPerYear: 120,
    hoursPerWeek: 4,
    assessment: {
      internalExamination: 60,
      continuousAssessment: 40
    },
    strands: [
      {
        id: "strand1",
        name: "Sustainable Farm Ecosystem",
        hours: 10,
        percentage: 9,
        topics: [
          {
            id: "11AGR1.1",
            name: "Natural and Farm Ecosystems",
            description: "Understanding natural and farmed ecosystems and the influence of farmers on them",
            weeks: "1-2",
            learningOutcomes: [
              "Define natural ecosystem and farm ecosystem",
              "Identify similarities between natural and farm ecosystems",
              "Describe features of natural and farm ecosystems",
              "Explain natural ecosystem as a result of interactions between organisms and environment",
              "Explain why farm ecosystem is an artificial ecosystem that requires human assistance",
              "Discuss the influence of the farmer on both ecosystems"
            ]
          },
          {
            id: "11AGR1.2",
            name: "Agricultural Management Systems",
            description: "Understanding agricultural management systems and the importance of diversification",
            weeks: "3-4",
            learningOutcomes: [
              "Define mono-cropping, multi-cropping, organic farming, and agro-forestry",
              "Describe features of each agricultural management system",
              "Explain ecological importance of organic farming and agro-forestry",
              "Discuss usefulness of diversification in agriculture",
              "Discuss financial and economic importance using specific examples"
            ]
          },
          {
            id: "11AGR1.3",
            name: "Impacts of Agriculture on Ecosystems",
            description: "Understanding the impacts of agricultural practices on natural ecosystems",
            weeks: "5",
            learningOutcomes: [
              "Identify agricultural practices harmful to natural ecosystems",
              "Express views on soil erosion and pollution from farming activities",
              "Predict future ecological impacts of farming practices on selected ecosystems"
            ]
          }
        ]
      },
      {
        id: "strand2",
        name: "Soil Management",
        hours: 20,
        percentage: 17,
        topics: [
          {
            id: "11AGR2.1",
            name: "Properties of Soils",
            description: "Understanding different properties of soil in relation to agriculture",
            weeks: "6-9",
            learningOutcomes: [
              "Define soil texture, structure, and pH",
              "Identify different soil types",
              "Describe physical, chemical, and biological properties of soil",
              "Carry out experiments to determine soil properties",
              "Classify soil organisms and explain their roles as decomposers",
              "Explain roles of bacteria species Rhizobia in nitrogen fixation",
              "Discuss roles of soil nutrients and deficiency symptoms"
            ]
          },
          {
            id: "11AGR2.2",
            name: "Soil Management Practices",
            description: "Understanding soil management practices used to improve farm soils",
            weeks: "10-12",
            learningOutcomes: [
              "Define agronomic practices, manures, compost, tillage, soil amendments, liming, drainage, irrigation",
              "Explain importance of applying agronomic practices to improve soils",
              "Explain how animal manures, green manure, and compost improve soil",
              "Analyse beneficial effects of tillage, amendments, liming, drainage, irrigation, and conservation"
            ]
          },
          {
            id: "11AGR2.3",
            name: "Soil Conservation Practices",
            description: "Understanding different types of soil conservation techniques",
            weeks: "13-14",
            learningOutcomes: [
              "Define farm productivity",
              "Identify soil conservation measures used locally and regionally",
              "List and describe features of different conservation measures",
              "Discuss advantages and disadvantages of conservation techniques"
            ]
          }
        ]
      },
      {
        id: "strand3",
        name: "Plant and Animal Production",
        hours: 45,
        percentage: 40,
        topics: [
          {
            id: "11AGR3.1",
            name: "Crop Establishment",
            description: "Understanding the process of establishing a crop",
            weeks: "15-17",
            learningOutcomes: [
              "Identify planting sites with proper agronomic characteristics",
              "Identify proper tillage tools and equipment",
              "Describe traditional and modern nursery techniques",
              "Analyse techniques used in nursery preparation and management",
              "Discuss whole process from soil cultivation to transplanting"
            ]
          },
          {
            id: "11AGR3.2",
            name: "Crop Growth and Development",
            description: "Understanding factors affecting growth and development in plants",
            weeks: "18-20",
            learningOutcomes: [
              "Identify biological, husbandry, and environmental factors affecting plant growth",
              "Explain how cultivar, pest/disease control, dressing, watering, pruning affect plants",
              "Explain how humidity, light, temperature affect plant development",
              "Discuss techniques used to manipulate growth including growth hormones"
            ]
          },
          {
            id: "11AGR3.3",
            name: "Crop Harvest and Post-harvest Handling",
            description: "Understanding harvesting and post-harvest handling processes",
            weeks: "21-22",
            learningOutcomes: [
              "Identify harvesting time and relate to fruit ripening stages",
              "Suggest techniques to minimize product loss during harvesting",
              "Describe post-harvest handling procedures and techniques",
              "Discuss importance of post-harvest handling for packaging and sale"
            ]
          },
          {
            id: "11AGR3.4",
            name: "Livestock Establishment",
            description: "Understanding inputs needed for rearing young livestock",
            weeks: "23-25",
            learningOutcomes: [
              "Identify husbandry and feeding requirements from birth to weaning",
              "Select proper feeds and water for young animals at different growth stages",
              "Formulate rations using Pearson's square method",
              "Relate animal welfare with housing conditions (lighting, floor space, warmth)",
              "Reflect on availability and costs of feeds for young livestock"
            ]
          },
          {
            id: "11AGR3.5",
            name: "Growth and Development of Livestock",
            description: "Understanding growth, development and production of livestock",
            weeks: "26-28",
            learningOutcomes: [
              "Identify biological, husbandry, and environmental factors affecting livestock",
              "Define husbandry tasks: feeding, castration, eye teeth cutting, breeding",
              "Explain how breed, health, husbandry, and environmental factors affect livestock",
              "Analyse growth data to identify growth rate and feed efficiency"
            ]
          },
          {
            id: "11AGR3.6",
            name: "Slaughtering and Post Handling",
            description: "Understanding processes of slaughtering and post-slaughter handling",
            weeks: "29-30",
            learningOutcomes: [
              "Define slaughtering, post slaughter, slaughter house, home slaughter, post mortem, branding, chilling",
              "Identify steps in slaughtering process of cattle, pig, goat and chicken",
              "Describe each slaughtering step used in home slaughter or slaughterhouse",
              "Discuss health issues, meat quality, and importance of hygiene",
              "Discuss importance of conservation of meat quality through post-slaughter handling"
            ]
          }
        ]
      },
      {
        id: "strand4",
        name: "Crop and Animal Protection",
        hours: 19,
        percentage: 17,
        topics: [
          {
            id: "11AGR4.1",
            name: "Types of Pests, Diseases and Weeds",
            description: "Understanding common and invasive species of weeds, parasites and diseases on livestock",
            weeks: "31-33",
            learningOutcomes: [
              "Define ecto-parasites, endo-parasites, propagation, invasive weed",
              "Distinguish between common and invasive species of weeds",
              "Identify and describe common weeds in Vanuatu",
              "Explain how weeds spread through propagation",
              "Identify selected ecto and endo-parasites affecting livestock",
              "Describe complete and incomplete life cycles of parasites",
              "Discuss control of parasites in relation to life cycle stages"
            ]
          },
          {
            id: "11AGR4.2",
            name: "Management of Pests and Diseases",
            description: "Understanding different measures used to control pests and diseases",
            weeks: "34-36",
            learningOutcomes: [
              "Identify biological, chemical, and cultural control methods",
              "Classify control measures into biological, chemical and cultural methods",
              "Describe advantages and disadvantages of pest control methods",
              "Discuss procedure for preparing and applying pesticides safely",
              "Analyse effects of different controls for major pests and diseases"
            ]
          },
          {
            id: "11AGR4.3",
            name: "Economics of Pest Management",
            description: "Understanding different types of pest management and economics of controlling them",
            weeks: "37-38",
            learningOutcomes: [
              "Define Integrated Pest Management (IPM)",
              "Describe features of IPM",
              "Relate IPM benefits to farm balanced ecosystem",
              "Estimate costs and financial benefits of different pest management types"
            ]
          }
        ]
      },
      {
        id: "strand5",
        name: "Farm Management and Economics",
        hours: 19,
        percentage: 17,
        topics: [
          {
            id: "11AGR5.1",
            name: "Costs, Returns and Profit",
            description: "Understanding economics and benefits of an agricultural enterprise",
            weeks: "39-41",
            learningOutcomes: [
              "Define production cost, returns, profit, financial record, projections",
              "Identify and calculate establishment and production costs",
              "Estimate returns using projections",
              "Determine profit using financial records",
              "Discuss reasons for increased cost of production in modern farms"
            ]
          },
          {
            id: "11AGR5.2",
            name: "Markets",
            description: "Understanding the concept of market and factors influencing market of a product",
            weeks: "42-43",
            learningOutcomes: [
              "Define market, demand, supply and price",
              "List market forces affecting the market of a product",
              "Explain how each factor can influence market of a product"
            ]
          },
          {
            id: "11AGR5.3",
            name: "Farm Management",
            description: "Understanding factors influencing farmer's decisions and role of record keeping",
            weeks: "44-45",
            learningOutcomes: [
              "Identify major factors influencing farmer's decision: Land, Labour, Capital",
              "Define record keeping and farm management",
              "Describe major factors influencing farmer's decisions",
              "Explain how choice of new technology can increase long-term profitability",
              "Discuss role of record keeping in good farm management"
            ]
          }
        ]
      }
    ]
  },
  year12: {
    title: "Year 12 Agriculture",
    description: "Advanced agricultural concepts covering integrated farm management, soil improvement, crop and livestock management, pest control economics, and market analysis",
    hoursPerYear: 120,
    hoursPerWeek: 4,
    assessment: {
      externalExamination: 60,
      internalAssessment: 40
    },
    strands: [
      {
        id: "strand1",
        name: "Sustainable Farm Ecosystem",
        hours: 10,
        percentage: 9,
        topics: [
          {
            id: "12AGR1.1",
            name: "Farm Ecosystems",
            description: "Understanding integrated farm management approaches geared towards profit making and sustainability",
            weeks: "1-2",
            learningOutcomes: [
              "Identify integrated farm management approaches that are environmentally friendly",
              "Define integrated farm management",
              "Describe sustainable farming and husbandry practices",
              "Compare extensive, semi-intensive and intensive farm management systems",
              "Explain how integrated farm management contributes to economic performance and environmental quality",
              "Evaluate local or national farm enterprises favoring sustainability"
            ]
          },
          {
            id: "12AGR1.2",
            name: "Traditional and Introduced Farming Systems in Vanuatu",
            description: "Understanding farming systems that maintain productivity of farm ecosystems in Vanuatu",
            weeks: "3-4",
            learningOutcomes: [
              "Define bush fallow, alley cropping, agro-forestry, ecological benefit",
              "Identify traditional systems of agriculture in Vanuatu and the region",
              "Describe ecological benefits of bush fallow",
              "Explain how traditional and introduced systems maintain farm productivity",
              "Explain effects of slash and burn method",
              "Discuss how traditional systems maintain farm ecosystems through species integration and legumes",
              "Evaluate differences between traditional and introduced farming systems"
            ]
          },
          {
            id: "12AGR1.3",
            name: "Management of Impacts of Farming on Ecosystems",
            description: "Understanding management of water and wastes in farms",
            weeks: "5",
            learningOutcomes: [
              "Define waste management and water management in farms",
              "Identify waste management methods at garden and plantation level",
              "Describe features and benefits of waste and water management",
              "Explain how wastes and water are managed in the farm",
              "Relate food production to water quantity and irrigation",
              "Evaluate methods to control water scarcity and maintain productivity"
            ]
          }
        ]
      },
      {
        id: "strand2",
        name: "Soil Management",
        hours: 20,
        percentage: 17,
        topics: [
          {
            id: "12AGR2.1",
            name: "Improvement of Soil Properties",
            description: "Understanding how to improve soil properties for farming purposes",
            weeks: "6-8",
            learningOutcomes: [
              "Define soil texture, structure, porosity",
              "List farming practices that help improve soil properties",
              "Describe ways to improve soil physical properties (granulation, aeration, root penetration, water holding capacity)",
              "Describe ways to improve soil chemical properties",
              "Discuss use of organic matter and compost to improve soil parameters",
              "Evaluate soil by identifying biological properties and relating to soil use"
            ]
          },
          {
            id: "12AGR2.2",
            name: "Agronomic Practices to Improve Soils",
            description: "Understanding use of agronomic practices to ensure long term primary production",
            weeks: "9-11",
            learningOutcomes: [
              "Define agronomic practices, tillage, organic manure",
              "List and describe agronomic practices used by farmers",
              "Describe advantages and disadvantages of tillage, organic manure, mulching",
              "Explain how to minimize soil erosion through contour tillage, mulching, cover crops, direct drilling",
              "Discuss how to improve soil properties by incorporating fertilizers or amendments"
            ]
          },
          {
            id: "12AGR2.3",
            name: "Soil Conservation Measures",
            description: "Understanding regionally approved soil conservation techniques",
            weeks: "12-14",
            learningOutcomes: [
              "Define soil erosion",
              "List approved soil conservation practices in Vanuatu and the region",
              "Describe approved practices and impacts of soil erosion",
              "Explain benefits of approved soil conservation practices",
              "Explain crop rotation commonly used in Vanuatu",
              "Discuss choices, challenges and benefits of conservation techniques in Pacific islands"
            ]
          }
        ]
      },
      {
        id: "strand3",
        name: "Plant and Animal Production",
        hours: 45,
        percentage: 40,
        topics: [
          {
            id: "12AGR3.1",
            name: "Seed Production",
            description: "Understanding the process of seed production",
            weeks: "15-17",
            learningOutcomes: [
              "Define replanting, seed harvesting, seed cleaning",
              "State importance of producing seeds for replanting",
              "List criteria for selecting plants and fruits for seeding",
              "Describe techniques for seed harvesting, cleaning, drying, storage, treatments",
              "Discuss whole process of seed production for replanting, distribution, storage or sale"
            ]
          },
          {
            id: "12AGR3.2",
            name: "Crop Management",
            description: "Understanding crop management process from planting to harvesting",
            weeks: "18-21",
            learningOutcomes: [
              "Identify crop management at soil level for root penetration, drainage, root quality",
              "Define crop management",
              "List ecological and husbandry factors affecting growth",
              "Develop timeline from planting to harvest indicating husbandry tasks",
              "Explain environmental factors affecting growth and development",
              "Discuss successful crop management steps and ways of improvement"
            ]
          },
          {
            id: "12AGR3.3",
            name: "Value Adding Products",
            description: "Understanding post-harvest handling and value adding processes",
            weeks: "22-23",
            learningOutcomes: [
              "Identify chronological steps in post-harvest handling to add value",
              "Define value added product",
              "Describe post-harvest handling, storage and packaging processes",
              "Discuss value added processes for different market opportunities",
              "Discuss importance of value added in agriculture products"
            ]
          },
          {
            id: "12AGR3.4",
            name: "Production of Livestock Offspring",
            description: "Understanding different systems of rearing and breeding livestock",
            weeks: "24-26",
            learningOutcomes: [
              "Define animal breeding, inbreeding, cross-breeding, linebreeding, selection",
              "Identify and describe different livestock breeding methods",
              "Describe advantages and disadvantages of breeding systems",
              "Explain effect of breeding in maintaining purity and increasing productivity",
              "Discuss selection of breeding animals considering inheritance of traits",
              "Describe different systems of rearing livestock on commercial farms"
            ]
          },
          {
            id: "12AGR3.5",
            name: "Livestock Management",
            description: "Understanding management process to improve growth and development of livestock",
            weeks: "27-30",
            learningOutcomes: [
              "Define body conformation and Food Conversion Efficiency",
              "Describe growth and development in chicken, pig, beef cattle",
              "Explain relationship between management practices and livestock development",
              "Describe importance of hygiene, feeding regime, housing environment",
              "Compare growth rates of different breeds",
              "Compare traditional and modern management systems",
              "Discuss proper selection of breed, feedstocks and water"
            ]
          },
          {
            id: "12AGR3.6",
            name: "Value Adding to End Products",
            description: "Understanding post-slaughter handling process to add value to products",
            weeks: "31-32",
            learningOutcomes: [
              "Identify chronological steps in post-slaughter handling",
              "Describe packaging, quality control, grading, quarantine requirements, freezing",
              "Explain importance of post-slaughter handling in preparing marketable product",
              "Discuss how processing techniques can add value to primary products"
            ]
          }
        ]
      },
      {
        id: "strand4",
        name: "Crop and Animal Protection",
        hours: 19,
        percentage: 17,
        topics: [
          {
            id: "12AGR4.1",
            name: "Pests and Diseases",
            description: "Understanding characteristics of common diseases and pests and behavior of parasites",
            weeks: "33-35",
            learningOutcomes: [
              "Identify common pests and diseases of crop and livestock",
              "Identify causes and signs of ill-health",
              "Describe features and mode of spread of common pests and diseases",
              "Explain physiological changes from pests and diseases",
              "Discuss how pests and diseases affect health and productivity"
            ]
          },
          {
            id: "12AGR4.2",
            name: "Control of Common Pests and Diseases",
            description: "Understanding most appropriate control measures for pests and diseases",
            weeks: "36-38",
            learningOutcomes: [
              "Identify traditional and modern methods of controlling pests and diseases",
              "Describe features, advantages and disadvantages of control methods",
              "Define Integrated Pest and Disease Management (IPM, IDM)",
              "Relate insect behavior at different lifecycle stages to control methods",
              "Discuss most appropriate control measures based on cost, environmental impact and sustainability"
            ]
          },
          {
            id: "12AGR4.3",
            name: "Economics of Controlling Selected Pests and Diseases",
            description: "Understanding economic reasons for controlling pests and diseases",
            weeks: "39-40",
            learningOutcomes: [
              "Identify effects of pests and diseases on farmer's financial status",
              "Describe and estimate economic loss due to pests and diseases",
              "Explain importance of cost effective and environmentally friendly measures",
              "Discuss economic implications in terms of product quality, movement and trade"
            ]
          }
        ]
      },
      {
        id: "strand5",
        name: "Farm Management and Economics",
        hours: 19,
        percentage: 17,
        topics: [
          {
            id: "12AGR5.1",
            name: "Cash Flow and Gross Margins Analysis",
            description: "Understanding concepts of cash flow, costs, returns, gross margins and budgeting",
            weeks: "41-43",
            learningOutcomes: [
              "Define gross margin and cash flow",
              "State purpose of calculating gross margin",
              "Describe use of cash flow to record money movement",
              "List components of gross margin analysis",
              "Explain how cash flow determines project's rate of return",
              "Prepare simple budget and calculate gross margin"
            ]
          },
          {
            id: "12AGR5.2",
            name: "Factors Influencing Primary Production",
            description: "Understanding influence of factors of primary production",
            weeks: "44-46",
            learningOutcomes: [
              "Define primary production, supply, demand and market price",
              "Name and describe market-changing factors affecting farm decisions",
              "Describe effects of supply, demand, price changes on primary production",
              "Explain effects of market access, seasonality on local products",
              "Discuss effects of market channels and quality requirements"
            ]
          },
          {
            id: "12AGR5.3",
            name: "Management of Tools, Equipment and Machinery",
            description: "Understanding management of tools, equipment and simple farm machinery",
            weeks: "47-48",
            learningOutcomes: [
              "Name tools, equipment and simple farm machinery in agricultural production",
              "List functions of tools, equipment and machinery",
              "Describe basic principles of keeping, maintaining and safety use",
              "Explain need for consistent repairing and routine maintenance",
              "Discuss importance of maintaining farm equipment"
            ]
          }
        ]
      }
    ]
  },
  year13: {
    title: "Year 13 Agriculture",
    description: "Advanced agricultural concepts covering global environmental issues, soil productivity, crop and livestock breeding, sustainable pest management, trade regulations and farm profitability",
    hoursPerYear: 135,
    hoursPerWeek: 4.5,
    assessment: {
      externalExamination: 60,
      internalAssessment: 40,
      internalComponents: {
        project: { name: "Extended Practical Investigation", weight: 16 },
        task1: { name: "Research", weight: 14 },
        task2: { name: "Shorter Practical Investigation", weight: 10 }
      }
    },
    strands: [
      {
        id: "strand1",
        name: "Sustainable Farm Ecosystem",
        hours: 10,
        percentage: 9,
        topics: [
          {
            id: "13AGR1.1",
            name: "Global Environmental Issues",
            description: "Understanding effects of global agricultural issues on farming",
            weeks: "1-2",
            learningOutcomes: [
              "Identify global agricultural issues affecting farming in Vanuatu",
              "Define genetic engineering and genetic resource",
              "Describe how genetic resource conservation affects agriculture",
              "Explain effects of invasive species and introduction of new species",
              "Discuss advantages and disadvantages of introducing new species",
              "Prepare and conduct a case study on a selected agricultural issue"
            ]
          },
          {
            id: "13AGR1.2",
            name: "Sustainable Management of Farm Ecosystems",
            description: "Understanding sustainable management techniques that maintain healthy farm ecosystems",
            weeks: "3-4",
            learningOutcomes: [
              "Define Organic Farming and sustainable management of natural resources",
              "Describe features of organic farming practices",
              "Describe sustainable management techniques: crop rotation, conservation tillage, pasture-based livestock husbandry",
              "Explain how sustainable farms protect biodiversity and foster healthy ecosystems",
              "Discuss how sustainable techniques maintain healthy ecosystems for future generations"
            ]
          },
          {
            id: "13AGR1.3",
            name: "Global Impacts of Farming on the Environment",
            description: "Understanding impacts of farming and climate change on farm land environment",
            weeks: "5",
            learningOutcomes: [
              "Identify impacts of farming on land: deforestation, soil degradation, pollutants, wastes",
              "State impacts of climate change on farm outputs and practices",
              "List and explain effects of climate change on selected crops and livestock",
              "Evaluate impacts of farming and climate change on local, national, regional and global farm land"
            ]
          }
        ]
      },
      {
        id: "strand2",
        name: "Soil Management",
        hours: 23,
        percentage: 18,
        topics: [
          {
            id: "13AGR2.1",
            name: "Soil Productivity",
            description: "Understanding use of soil productivity assessment approaches and ways of improvement",
            weeks: "6-9",
            learningOutcomes: [
              "Define soil productivity",
              "Identify factors that foster or reduce soil productivity",
              "Describe importance of soil texture, depth, pH, organic matter content and fertility",
              "Explain how each factor affects soil productivity",
              "Explain how drainage, weeds, insects, diseases, drought exposure affect productivity",
              "Propose soil amendments to improve soil productivity based on assessment results"
            ]
          },
          {
            id: "13AGR2.2",
            name: "Soil Fertility and Cultivation Improvement",
            description: "Understanding soil fertility, cultivation techniques and improvement",
            weeks: "10-12",
            learningOutcomes: [
              "Conduct simple soil pH and soil tests to determine fertility status",
              "Propose ways to improve soil fertility according to experiment results",
              "Describe cultivation techniques used in gardens",
              "Assess impacts of cultivation techniques on soil fertility",
              "Propose soil cultivation techniques that foster soil fertility"
            ]
          },
          {
            id: "13AGR2.3",
            name: "Soil Conservation Economics and Impacts",
            description: "Understanding soil conservation impacts on farming and the environment",
            weeks: "13-14",
            learningOutcomes: [
              "Identify losses in farm productivity due to uncontrolled soil erosion",
              "Identify costs associated with soil conservation techniques",
              "Discuss impacts of soil conservation on farm production and profitability",
              "Evaluate economic and agronomic benefits of soil conservation on the environment"
            ]
          }
        ]
      },
      {
        id: "strand3",
        name: "Plant and Animal Production",
        hours: 45,
        percentage: 37,
        topics: [
          {
            id: "13AGR3.1",
            name: "Crop Breeding",
            description: "Understanding the process of crop breeding",
            weeks: "15-17",
            learningOutcomes: [
              "Identify main steps in breeding new seeds in a research facility",
              "Define hybridization",
              "List advantages and disadvantages of different crop breeding methods",
              "Explain selection, hybridization, and line breeding as crop improvement techniques",
              "Design a simple crop breeding programme for improved crops with desirable traits"
            ]
          },
          {
            id: "13AGR3.2",
            name: "Schedule of Operations",
            description: "Understanding different schedule of operations of five selected crops",
            weeks: "18-20",
            learningOutcomes: [
              "Sketch crop production processes for five selected crops",
              "Schedule activities for each production process",
              "Categorize activities as managerial, husbandry, or environmental inputs",
              "Design and develop schedule of operations for five selected crops"
            ]
          },
          {
            id: "13AGR3.3",
            name: "Marketing of Crop Products",
            description: "Understanding post-harvest handling and marketing techniques",
            weeks: "21-22",
            learningOutcomes: [
              "Identify post-harvest handling processes for quality marketable products",
              "Describe post-harvest handling processes",
              "Identify marketing techniques to flow products to consumers",
              "Discuss importance of post-harvest handling processes"
            ]
          },
          {
            id: "13AGR3.4",
            name: "Livestock Breeding",
            description: "Understanding main techniques used in animal breeding",
            weeks: "23-25",
            learningOutcomes: [
              "Define animal breeding, selection, pure breed, line breeding, inbreeding, crossbreeding, hybridization, artificial insemination",
              "List advantages and disadvantages of each breeding technique",
              "Draw schematic diagrams of crossbreeding and inbreeding",
              "Describe artificial insemination techniques, advantages and disadvantages"
            ]
          },
          {
            id: "13AGR3.5",
            name: "Livestock Schedule of Operations",
            description: "Understanding different schedule of operations of three selected livestock",
            weeks: "26-27",
            learningOutcomes: [
              "Sketch production processes for three selected livestock",
              "Schedule activities for each production process",
              "Categorize activities as managerial, husbandry, or environmental inputs",
              "Design schedule of operations for three selected livestock"
            ]
          },
          {
            id: "13AGR3.6",
            name: "Marketing of Animal Products",
            description: "Understanding post-slaughter handling and marketing techniques",
            weeks: "28-29",
            learningOutcomes: [
              "Identify post-slaughter handling processes for quality marketable products",
              "Describe and summarize each post-slaughter handling process",
              "Discuss importance of post-slaughter handling processes",
              "Design marketing technique to flow products to consumers"
            ]
          },
          {
            id: "13AGR3.7",
            name: "Processing of a Primary Product",
            description: "Investigating how a primary product is processed in the community",
            weeks: "30-32",
            learningOutcomes: [
              "State aim, rationale and hypothesis of investigation",
              "Outline background information and describe method used",
              "Use logbook to record developments",
              "Process data with tables, graphs and calculations",
              "Interpret data to show trends, relationships and patterns",
              "Draw conclusions linked to hypothesis and evaluate findings"
            ]
          }
        ]
      },
      {
        id: "strand4",
        name: "Crop and Animal Protection",
        hours: 23,
        percentage: 18,
        topics: [
          {
            id: "13AGR4.1",
            name: "Sustainable Pest Management Systems",
            description: "Understanding sustainable pest management systems",
            weeks: "33-35",
            learningOutcomes: [
              "Identify and describe sustainable pest management systems",
              "Explain importance of ecologically sustainable pest control measures",
              "Compare and contrast biological and cultural control measures",
              "Justify Integrated Pest Management (IPM) techniques and environmental benefits"
            ]
          },
          {
            id: "13AGR4.2",
            name: "Biosecurity Regulations and Laws",
            description: "Understanding different biosecurity regulations and environmental laws affecting farming",
            weeks: "36-38",
            learningOutcomes: [
              "Define biosecurity regulations",
              "List quarantine laws on export and import of agricultural items",
              "Describe environmental laws with direct effects on farming",
              "Explain effects of quarantine and environmental laws on farming nationally",
              "Evaluate effects on regional trade of agricultural products"
            ]
          },
          {
            id: "13AGR4.3",
            name: "Effects of Pests and Disease on Trade",
            description: "Understanding national and international laws that reduce spread of pests and diseases",
            weeks: "39-40",
            learningOutcomes: [
              "Identify impacts of pests and diseases on trade",
              "State reasons for harmonizing quarantine laws in the region",
              "List values of good reporting systems for controlling disease spread",
              "Explain importance of import and export laws controlling disease spread",
              "Evaluate effectiveness of laws in managing pest and disease spread"
            ]
          }
        ]
      },
      {
        id: "strand5",
        name: "Farm Management and Economics",
        hours: 23,
        percentage: 18,
        topics: [
          {
            id: "13AGR5.1",
            name: "Profitability in Farming",
            description: "Understanding economic profitability of a farming enterprise",
            weeks: "41-43",
            learningOutcomes: [
              "Name five financial tools for an enterprise",
              "Use financial tools to determine profitability",
              "Define partial budget and list its components",
              "Carry out partial budgeting exercise for two enterprises",
              "Calculate cost efficiency and analyse gross margins to find most profitable enterprise"
            ]
          },
          {
            id: "13AGR5.2",
            name: "Trade in Agricultural Products",
            description: "Understanding market channels and marketing of agricultural products",
            weeks: "44-46",
            learningOutcomes: [
              "Identify marketing pathways from producers to consumers",
              "Identify regional trade organisations regulating agricultural products",
              "Illustrate different ways of distributing and selling products",
              "Explain how products are marketed through national and regional trade agreements",
              "Discuss WTO's function in free trade and benefits to Vanuatu",
              "Evaluate WTO's principles in global rules of trade"
            ]
          },
          {
            id: "13AGR5.3",
            name: "Management Regulations Affecting Agriculture Industry",
            description: "Understanding regulations and laws relating to agriculture industry",
            weeks: "47-48",
            learningOutcomes: [
              "State government interventions in market of agricultural products",
              "Describe national controls on movement and trade of agricultural items",
              "Differentiate between regulation and laws",
              "Describe laws and regulations relating to farm production and marketing",
              "Explain importance of having legislations relating to farm production",
              "Evaluate effectiveness of national agricultural policies"
            ]
          }
        ]
      }
    ]
  }
};

export default agricultureCurriculum;
